/**
 * LocalStorage utilities for guest layout and Live Zone preferences.
 * These are used for non-authenticated users.
 * 
 * Keys:
 * - allwrite_layout_preferences: { homepage, categoryPage, articleDetail }
 * - allwrite_livezone_preferences: { enabled, position, contentTypes, ... }
 */

const LAYOUT_PREFS_KEY = 'allwrite_layout_preferences';
const LIVEZONE_PREFS_KEY = 'allwrite_livezone_preferences';

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable() {
    try {
        const test = '__localStorage_test__';
        window.localStorage.setItem(test, test);
        window.localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Get layout preferences from localStorage
 * @returns {Object|null} Layout preferences or null if not set
 */
export function getGuestLayoutPreferences() {
    if (!isLocalStorageAvailable()) return null;
    try {
        const stored = window.localStorage.getItem(LAYOUT_PREFS_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (e) {
        console.warn('Failed to read layout preferences from localStorage:', e);
        return null;
    }
}

/**
 * Set layout preferences in localStorage
 * @param {Object} preferences - { homepage?, categoryPage?, articleDetail? }
 */
export function setGuestLayoutPreferences(preferences) {
    if (!isLocalStorageAvailable()) return;
    try {
        const current = getGuestLayoutPreferences() || {};
        const updated = { ...current, ...preferences };
        window.localStorage.setItem(LAYOUT_PREFS_KEY, JSON.stringify(updated));
    } catch (e) {
        console.warn('Failed to save layout preferences to localStorage:', e);
    }
}

/**
 * Get Live Zone preferences from localStorage
 * @returns {Object|null} Live Zone preferences or null if not set
 */
export function getGuestLiveZonePreferences() {
    if (!isLocalStorageAvailable()) return null;
    try {
        const stored = window.localStorage.getItem(LIVEZONE_PREFS_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (e) {
        console.warn('Failed to read Live Zone preferences from localStorage:', e);
        return null;
    }
}

/**
 * Set Live Zone preferences in localStorage
 * @param {Object} preferences - Live Zone config
 */
export function setGuestLiveZonePreferences(preferences) {
    if (!isLocalStorageAvailable()) return;
    try {
        const current = getGuestLiveZonePreferences() || {};
        // Deep merge for mobileOverrides
        const updated = {
            ...current,
            ...preferences,
            mobileOverrides: {
                ...(current.mobileOverrides || {}),
                ...(preferences.mobileOverrides || {})
            }
        };
        window.localStorage.setItem(LIVEZONE_PREFS_KEY, JSON.stringify(updated));
    } catch (e) {
        console.warn('Failed to save Live Zone preferences to localStorage:', e);
    }
}

/**
 * Clear all guest preferences (e.g., when user logs in)
 */
export function clearGuestPreferences() {
    if (!isLocalStorageAvailable()) return;
    try {
        window.localStorage.removeItem(LAYOUT_PREFS_KEY);
        window.localStorage.removeItem(LIVEZONE_PREFS_KEY);
    } catch (e) {
        console.warn('Failed to clear guest preferences:', e);
    }
}

/**
 * Initialize Redux state from localStorage for guest users
 * Returns merged preferences if guest has saved prefs, otherwise returns defaults
 * @param {Object} ssrPreferences - Server-provided defaults
 * @param {boolean} isAuthenticated - Whether user is logged in
 * @returns {Object} { layoutPreferences, liveZonePreferences }
 */
export function initializeGuestPreferences(ssrPreferences, isAuthenticated) {
    // If user is authenticated, their prefs come from the server
    if (isAuthenticated) {
        return {
            layoutPreferences: ssrPreferences.userLayoutPreferences || {},
            liveZonePreferences: ssrPreferences.userLiveZonePreferences || {}
        };
    }

    // For guests, merge localStorage with SSR defaults
    const storedLayouts = getGuestLayoutPreferences();
    const storedLiveZone = getGuestLiveZonePreferences();

    return {
        layoutPreferences: {
            ...(ssrPreferences.userLayoutPreferences || {}),
            ...(storedLayouts || {})
        },
        liveZonePreferences: {
            ...(ssrPreferences.userLiveZonePreferences || {}),
            ...(storedLiveZone || {}),
            mobileOverrides: {
                ...(ssrPreferences.userLiveZonePreferences?.mobileOverrides || {}),
                ...(storedLiveZone?.mobileOverrides || {})
            }
        }
    };
}
